import React, { useState, useEffect, useRef, useCallback } from 'react';
import { adLabsAPI } from '../../utils/api';

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatHms(seconds) {
  if (!seconds || seconds <= 0) return '00:00:00';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  return [h, m, s].map(v => String(v).padStart(2, '0')).join(':');
}

// ─── keyframes injection ──────────────────────────────────────────────────────

if (typeof document !== 'undefined' && !document.getElementById('adlab-keyframes')) {
  const style = document.createElement('style');
  style.id = 'adlab-keyframes';
  style.textContent = `
    @keyframes adlab-spin { to { transform: rotate(360deg); } }
    @keyframes adlab-pulse { 0%,100% { opacity:1; transform:scale(1); } 50% { opacity:0.6; transform:scale(1.4); } }
    @keyframes adlab-breathe { 0%,100% { box-shadow: 0 0 0 0 rgba(34,197,94,0.4); } 50% { box-shadow: 0 0 0 8px rgba(34,197,94,0); } }
  `;
  document.head.appendChild(style);
}

// ─── design tokens (aligned with SecHire design-system.css) ──────────────────

const C = {
  // Backgrounds — light theme matching --bg-primary / --bg-secondary
  bg:       '#f8f9fa',   // --bg-secondary
  surface:  '#ffffff',   // --bg-primary
  surface2: '#f5f5f5',   // --gray-100
  // Borders — matching --border-light / --border-medium
  border:   '#dee2e6',   // --border-light
  border2:  '#ced4da',   // --border-medium
  // Brand blue — --primary-600 / --primary-700
  blue:     '#1e88e5',   // --primary-600
  blueDim:  '#1976d2',   // --primary-700
  // Success green — --success / --success-dark
  green:    '#28a745',   // --success
  greenDim: '#218838',
  greenBg:  '#d1e7dd',   // --success-light
  greenText:'#0a3622',   // --success-dark
  // Warning — --warning
  yellow:   '#ffc107',   // --warning
  yellowBg: '#fff3cd',   // --warning-light
  yellowText:'#856404',  // --warning-dark
  // Danger — --danger
  red:      '#dc3545',   // --danger
  redBg:    '#f8d7da',   // --danger-light
  redText:  '#721c24',   // --danger-dark
  // Text — --text-primary / --text-secondary / --text-tertiary
  text:     '#212529',   // --text-primary
  textDim:  '#6c757d',   // --text-secondary
  textMuted:'#adb5bd',   // --text-tertiary
  // Typography
  fontHeading: "'Space Grotesk',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  fontBody:    "'Inter',-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif",
  mono:        "'JetBrains Mono','Courier New',monospace",
};

const S = {
  // Full-bleed breakout: escapes the parent 800px container to use full viewport width
  bleed: {
    position: 'relative',
    left: '50%',
    right: '50%',
    marginLeft: '-50vw',
    marginRight: '-50vw',
    width: '100vw',
    background: C.bg,
    color: C.text,
    fontFamily: C.fontBody,
    paddingBottom: '3rem',
  },
  inner: {
    maxWidth: '1100px',
    margin: '0 auto',
    padding: '1.5rem 2rem 0',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: '1fr 300px',
    gap: '1rem',
    alignItems: 'start',
  },
  card: {
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: '8px',
    padding: '1.25rem',
    marginBottom: '1rem',
    boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
  },
  sectionLabel: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: C.textMuted,
    marginBottom: '0.75rem',
  },
  badge: (color) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '3px 10px',
    borderRadius: '4px',
    fontSize: '0.72rem',
    fontWeight: 700,
    letterSpacing: '0.04em',
    background:
      color === 'green'  ? C.greenBg :
      color === 'yellow' ? C.yellowBg :
      color === 'red'    ? C.redBg :
                           C.surface2,
    color:
      color === 'green'  ? C.greenText :
      color === 'yellow' ? C.yellowText :
      color === 'red'    ? C.redText :
                           C.textDim,
    border: `1px solid ${
      color === 'green'  ? C.green :
      color === 'yellow' ? C.yellow :
      color === 'red'    ? C.red :
                           C.border2
    }`,
  }),
  connectBtn: {
    width: '100%',
    padding: '12px 20px',
    borderRadius: '6px',
    border: `1px solid ${C.blueDim}`,
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
    background: C.blue,
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    transition: 'background 0.2s ease',
    fontFamily: C.fontBody,
  },
  submitBtn: {
    width: '100%',
    padding: '10px 16px',
    borderRadius: '6px',
    border: `1px solid ${C.greenDim}`,
    cursor: 'pointer',
    fontSize: '0.875rem',
    fontWeight: 600,
    background: C.green,
    color: '#fff',
    transition: 'background 0.2s ease',
    fontFamily: C.fontBody,
  },
  resetBtn: {
    width: '100%',
    padding: '9px 16px',
    borderRadius: '6px',
    border: `1px solid ${C.border2}`,
    cursor: 'pointer',
    fontSize: '0.82rem',
    fontWeight: 600,
    background: C.surface,
    color: C.textDim,
    transition: 'background 0.2s ease',
    fontFamily: C.fontBody,
  },
  flagInput: {
    width: '100%',
    padding: '10px 12px',
    background: C.surface,
    border: `1px solid ${C.border2}`,
    borderRadius: '6px',
    color: C.text,
    fontFamily: C.mono,
    fontSize: '0.82rem',
    outline: 'none',
    marginBottom: '0.6rem',
    boxSizing: 'border-box',
  },
  mono: {
    fontFamily: C.mono,
    fontSize: '0.85rem',
    color: C.text,
  },
  entryIpBox: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: '6px',
    padding: '12px 16px',
    marginBottom: '1rem',
  },
  spinner: {
    display: 'inline-block',
    width: '14px',
    height: '14px',
    border: `2px solid ${C.border2}`,
    borderTopColor: C.blue,
    borderRadius: '50%',
    animation: 'adlab-spin 0.8s linear infinite',
    flexShrink: 0,
  },
  pulseDot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    background: C.green,
    animation: 'adlab-pulse 2s ease-in-out infinite',
    flexShrink: 0,
  },
  progressBar: (pct) => ({
    height: '6px',
    borderRadius: '3px',
    background: C.border2,
    overflow: 'hidden',
    position: 'relative',
  }),
  progressFill: (pct) => ({
    height: '100%',
    width: `${pct}%`,
    background: C.green,
    borderRadius: '3px',
    transition: 'width 0.4s ease',
  }),
  countdownBox: {
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: '6px',
    padding: '10px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '0.75rem',
  },
  toast: (ok) => ({
    padding: '8px 12px',
    borderRadius: '6px',
    fontSize: '0.8rem',
    fontWeight: 600,
    background: ok ? C.greenBg : C.redBg,
    color: ok ? C.greenText : C.redText,
    border: `1px solid ${ok ? C.green : C.red}`,
    marginTop: '0.5rem',
    textAlign: 'center',
  }),
  credRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 0',
    borderBottom: `1px solid ${C.border}`,
  },
  credLabel: {
    fontSize: '0.75rem',
    color: C.textMuted,
    minWidth: '80px',
  },
  credVal: {
    fontFamily: C.mono,
    fontSize: '0.82rem',
    color: C.text,
    flex: 1,
  },
  copyBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '2px 6px',
    borderRadius: '4px',
    color: C.textMuted,
    fontSize: '0.72rem',
    transition: 'color 0.15s',
  },
};

// ─── copy helper ──────────────────────────────────────────────────────────────

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      style={{ ...S.copyBtn, color: copied ? C.green : C.textMuted }}
      onClick={() => {
        navigator.clipboard.writeText(text).catch(() => {});
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      title="Copy"
    >
      {copied ? '✓' : 'copy'}
    </button>
  );
}

// ─── credential row ───────────────────────────────────────────────────────────

function CredRow({ label, value, secret }) {
  const [show, setShow] = useState(!secret);
  return (
    <div style={S.credRow}>
      <span style={S.credLabel}>{label}</span>
      <span style={S.credVal}>
        {secret && !show ? '••••••••••••' : value}
      </span>
      {secret && (
        <button
          style={{ ...S.copyBtn }}
          onClick={() => setShow(s => !s)}
          title={show ? 'Hide' : 'Reveal'}
        >
          {show ? 'hide' : 'show'}
        </button>
      )}
      <CopyButton text={value} />
    </div>
  );
}

// ─── topology map ─────────────────────────────────────────────────────────────

const TOPOLOGY_BY_SCENARIO = {
  // Docker labs — VPN entry, flat 10.10.10.0/24
  'default': {
    subnet: '10.10.10.0/24  ·  Internal Network',
    nodes: [
      { id: 'you',   label: 'YOU',   sub: 'VPN Client',          ip: '—',           x: 220, y: 30,  fill: '#e3f2fd', border: '#1e88e5', role: null },
      { id: 'ws01',  label: 'WS01',  sub: 'Workstation · Entry', ip: '10.10.10.30', x: 220, y: 130, fill: '#d1e7dd', border: '#28a745', role: 'ENTRY' },
      { id: 'srv01', label: 'SRV01', sub: 'File Server',         ip: '10.10.10.20', x: 390, y: 130, fill: '#fff3cd', border: '#ffc107', role: null },
      { id: 'dc01',  label: 'DC01',  sub: 'Domain Controller',   ip: '10.10.10.10', x: 220, y: 235, fill: '#cfe2ff', border: '#0d6efd', role: 'HIGH VALUE' },
    ],
    edges: [
      { from: 'you',  to: 'ws01',  label: 'VPN/SSH',   dashed: true },
      { from: 'ws01', to: 'srv01', label: 'SMB',        dashed: false },
      { from: 'ws01', to: 'dc01',  label: 'Kerb/LDAP',  dashed: false },
    ],
  },
  // Hollow Crown — browser terminal entry via Kali, 3 VLANs
  'hollow-crown': {
    subnet: 'VLAN10/20/30  ·  corp.local',
    nodes: [
      { id: 'you',   label: 'YOU',   sub: 'Browser Terminal',    ip: '—',            x: 220, y: 25,  fill: '#e3f2fd', border: '#1e88e5', role: null },
      { id: 'kali',  label: 'Kali',  sub: 'Attacker · VLAN10',   ip: '10.10.10.99',  x: 220, y: 110, fill: '#d1e7dd', border: '#28a745', role: 'ENTRY' },
      { id: 'ws01',  label: 'WS01',  sub: 'Win10 · VLAN10',      ip: '10.10.10.50',  x: 390, y: 110, fill: '#fff3cd', border: '#ffc107', role: null },
      { id: 'srv01', label: 'SRV01', sub: 'MSSQL · VLAN20',      ip: '10.20.20.10',  x: 390, y: 200, fill: '#ffe8cc', border: '#fd7e14', role: null },
      { id: 'dc01',  label: 'DC01',  sub: 'DC · VLAN30',         ip: '10.30.30.10',  x: 220, y: 200, fill: '#cfe2ff', border: '#0d6efd', role: 'HIGH VALUE' },
    ],
    edges: [
      { from: 'you',   to: 'kali',  label: 'Guacamole', dashed: true },
      { from: 'kali',  to: 'ws01',  label: 'RDP/SMB',   dashed: false },
      { from: 'ws01',  to: 'srv01', label: 'MSSQL',     dashed: false },
      { from: 'kali',  to: 'dc01',  label: 'LDAP/Kerb', dashed: false },
      { from: 'srv01', to: 'dc01',  label: 'WinRM',     dashed: false },
    ],
  },
};

function LabTopologyMap({ ownedNodes = [], scenario = 'default' }) {
  const isOwned = (name) => ownedNodes.includes(name);

  const topo  = TOPOLOGY_BY_SCENARIO[scenario] || TOPOLOGY_BY_SCENARIO['default'];
  const nodes = topo.nodes;
  const edges = topo.edges;

  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]));
  const W = 110, H = 66;

  const roleColor  = { 'ENTRY': '#28a745', 'HIGH VALUE': '#0d6efd' };
  const roleText   = { 'ENTRY': '#0a3622', 'HIGH VALUE': '#084298' };

  return (
    <div style={{ background: C.surface2, borderRadius: '8px', padding: '1rem', border: `1px solid ${C.border}` }}>
      <svg viewBox="0 0 500 290" style={{ width: '100%', maxHeight: '290px' }}
        fontFamily={C.mono}>
        <defs>
          <pattern id="topo-grid" width="20" height="20" patternUnits="userSpaceOnUse">
            <path d="M 20 0 L 0 0 0 20" fill="none" stroke={C.border} strokeWidth="0.5" />
          </pattern>
          <marker id="topo-arrow" markerWidth="7" markerHeight="7" refX="5" refY="3" orient="auto">
            <path d="M0,0 L0,6 L7,3 z" fill={C.textMuted} />
          </marker>
        </defs>

        <rect width="500" height="290" fill="url(#topo-grid)" />

        {/* Internal subnet box */}
        <rect x="50" y="90" width="410" height="170" rx="6"
          fill="none" stroke={C.border2} strokeWidth="1" strokeDasharray="4 3" />
        <text x="255" y="107" textAnchor="middle" fontSize="9" fill={C.textMuted} fontWeight="700">
          {topo.subnet}
        </text>

        {/* Edges */}
        {edges.map(e => {
          const a = nodeMap[e.from], b = nodeMap[e.to];
          if (!a || !b) return null;
          const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
          return (
            <g key={`${e.from}-${e.to}`}>
              <line
                x1={a.x} y1={a.y + H / 2}
                x2={b.x} y2={b.y - H / 2}
                stroke={C.border2} strokeWidth="1.5"
                strokeDasharray={e.dashed ? '5 3' : '0'}
                markerEnd="url(#topo-arrow)"
              />
              <rect x={mx - 28} y={my - 8} width="56" height="14" rx="3" fill={C.surface2} />
              <text x={mx} y={my + 4} textAnchor="middle" fontSize="8" fill={C.textMuted}>{e.label}</text>
            </g>
          );
        })}

        {/* Nodes */}
        {nodes.map(node => {
          const owned = isOwned(node.id);
          const nx = node.x - W / 2, ny = node.y - H / 2;
          const bc = owned ? C.green : node.border;
          return (
            <g key={node.id}>
              <rect x={nx} y={ny} width={W} height={H} rx="7"
                fill={owned ? C.greenBg : node.fill}
                stroke={bc} strokeWidth={owned ? 2 : 1.5} />

              {/* Role badge */}
              {node.role && (
                <>
                  <rect x={nx + 4} y={ny + 4} width={node.role.length * 5.5 + 6} height={13} rx="2"
                    fill={roleColor[node.role]} opacity="0.85" />
                  <text x={nx + 7} y={ny + 13} fontSize="7.5" fill={roleText[node.role]} fontWeight="700">
                    {node.role}
                  </text>
                </>
              )}

              {/* Owned badge */}
              {owned && (
                <>
                  <rect x={nx + W / 2 - 22} y={ny + H - 15} width="44" height="13" rx="2" fill={C.green} />
                  <text x={node.x} y={ny + H - 5} textAnchor="middle" fontSize="8" fill="#fff" fontWeight="800">★ OWNED</text>
                </>
              )}

              <text x={node.x} y={node.y - 8} textAnchor="middle" fontSize="12" fontWeight="800" fill={bc}>{node.label}</text>
              <text x={node.x} y={node.y + 8}  textAnchor="middle" fontSize="9.5" fill={C.textDim}>{node.ip}</text>
              <text x={node.x} y={node.y + 22} textAnchor="middle" fontSize="8" fill={C.textMuted}>{node.sub}</text>
            </g>
          );
        })}
      </svg>

      <div style={{ display: 'flex', gap: '1rem', marginTop: '0.5rem', flexWrap: 'wrap' }}>
        {[
          { color: '#28a745', label: 'Entry Point' },
          { color: '#0d6efd', label: 'High Value' },
          { color: '#ffc107', label: 'Lateral Target' },
          { color: C.green,   label: 'Owned' },
        ].map(({ color, label }) => (
          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <div style={{ width: 9, height: 9, borderRadius: 2, background: color }} />
            <span style={{ fontSize: '0.7rem', color: C.textMuted }}>{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── provisioning game ────────────────────────────────────────────────────────

const TRIVIA = [
  { q: 'What does AS-REP roasting exploit?',            a: 'Accounts with Kerberos pre-auth disabled',   wrong: ['Weak NTLM hashes', 'SMB signing', 'GPO misconfigs'] },
  { q: 'What port does LDAP run on?',                   a: '389',                                        wrong: ['445', '8080', '3389'] },
  { q: 'What tool requests TGS tickets for Kerberoast?',a: 'GetUserSPNs.py (Impacket)',                  wrong: ['Mimikatz', 'BloodHound', 'CrackMapExec'] },
  { q: 'What does xp_cmdshell do in MSSQL?',           a: 'Executes OS shell commands',                 wrong: ['Dumps password hashes', 'Lists AD users', 'Scans open ports'] },
  { q: 'LSA secrets are stored where?',                 a: 'HKLM\\SECURITY\\Policy\\Secrets',            wrong: ['C:\\Windows\\Temp', 'NTDS.dit', 'SAM database'] },
  { q: 'What does mimikatz sekurlsa::logonpasswords do?',a: 'Dumps plaintext creds from LSASS memory',  wrong: ['Scans for SPNs', 'Cracks NTLM hashes', 'Bruteforces RDP'] },
  { q: 'WinRM uses which default HTTP port?',           a: '5985',                                       wrong: ['3389', '445', '22'] },
  { q: 'BloodHound visualises what?',                   a: 'AD attack paths to Domain Admin',            wrong: ['Network topology', 'Open firewall ports', 'Process memory'] },
  { q: 'What is a Backup Operators privilege abuse?',   a: 'Read any file including NTDS.dit via wbadmin',wrong: ['Write to SYSVOL', 'Add users to Domain Admins', 'Enable WinRM'] },
  { q: 'ESC1 in ADCS allows what?',                     a: 'Enroll a cert with arbitrary SAN → impersonate any user', wrong: ['Delete certificate templates', 'Disable TLS', 'Read CA private key'] },
];

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const PROVISION_STEPS = [
  'Allocating isolated VLANs...',
  'Creating COW overlay disks...',
  'Booting DC01 (Domain Controller)...',
  'Booting SRV01 (MSSQL Server)...',
  'Booting WS01 (Workstation)...',
  'Joining machines to corp.local...',
  'Configuring AD users and ACLs...',
  'Starting Kali attacker container...',
  'Planting flags...',
  'Lab almost ready...',
];

function ProvisioningScreen({ loadError }) {
  const [stepIdx, setStepIdx]       = React.useState(0);
  const [qIdx, setQIdx]             = React.useState(() => Math.floor(Math.random() * TRIVIA.length));
  const [choices, setChoices]       = React.useState([]);
  const [selected, setSelected]     = React.useState(null);
  const [score, setScore]           = React.useState(0);
  const [streak, setStreak]         = React.useState(0);
  const [showResult, setShowResult] = React.useState(false);

  // Shuffle choices whenever question changes
  React.useEffect(() => {
    const q = TRIVIA[qIdx];
    setChoices(shuffle([q.a, ...q.wrong]));
    setSelected(null);
    setShowResult(false);
  }, [qIdx]);

  // Advance provision step every 18s
  React.useEffect(() => {
    const t = setInterval(() => setStepIdx(i => Math.min(i + 1, PROVISION_STEPS.length - 1)), 18000);
    return () => clearInterval(t);
  }, []);

  function pick(choice) {
    if (selected !== null) return;
    setSelected(choice);
    setShowResult(true);
    const correct = choice === TRIVIA[qIdx].a;
    if (correct) { setScore(s => s + 1); setStreak(s => s + 1); }
    else { setStreak(0); }
  }

  function next() {
    setQIdx(i => (i + 1) % TRIVIA.length);
  }

  const q = TRIVIA[qIdx];
  const correct = selected === q.a;

  return (
    <div style={{ ...S.bleed, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '3rem', paddingBottom: '3rem' }}>
      <div style={{ maxWidth: '520px', width: '100%', padding: '0 1.5rem' }}>

        {/* ── spinner + step ── */}
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ width: '44px', height: '44px', border: `3px solid ${C.border2}`, borderTopColor: C.blue, borderRadius: '50%', animation: 'adlab-spin 1s linear infinite', margin: '0 auto 1rem' }} />
          <h2 style={{ color: C.text, fontSize: '1.05rem', fontWeight: 700, margin: '0 0 0.3rem' }}>Starting Lab Environment</h2>
          <p style={{ color: C.textMuted, fontSize: '0.8rem', margin: 0 }}>{PROVISION_STEPS[stepIdx]}</p>
        </div>

        {/* ── trivia card ── */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: '12px', padding: '1.25rem 1.5rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.9rem' }}>
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: C.blue, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Hacking Trivia — pass the time!
            </span>
            <span style={{ fontSize: '0.75rem', color: C.textMuted }}>
              {score} correct{streak >= 2 ? ` 🔥 ${streak} streak` : ''}
            </span>
          </div>

          <p style={{ color: C.text, fontSize: '0.9rem', fontWeight: 600, lineHeight: 1.45, marginBottom: '1rem' }}>{q.q}</p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {choices.map((c) => {
              let bg = C.surface2, border = C.border, color = C.text;
              if (showResult) {
                if (c === q.a)           { bg = C.greenBg;  border = C.green;  color = C.greenText; }
                else if (c === selected) { bg = C.redBg;    border = C.red;    color = C.redText;   }
              } else if (selected === c) {
                bg = '#e8f0fd'; border = C.blue;
              }
              return (
                <button key={c} onClick={() => pick(c)} style={{
                  background: bg, border: `1.5px solid ${border}`, color, borderRadius: '7px',
                  padding: '0.55rem 0.85rem', fontSize: '0.84rem', textAlign: 'left', cursor: showResult ? 'default' : 'pointer',
                  fontFamily: C.fontBody, transition: 'all 0.15s',
                }}>
                  {c}
                </button>
              );
            })}
          </div>

          {showResult && (
            <div style={{ marginTop: '0.9rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontSize: '0.82rem', color: correct ? C.green : C.red, fontWeight: 600 }}>
                {correct ? '✓ Correct!' : `✗ Answer: ${q.a}`}
              </span>
              <button onClick={next} style={{
                background: C.blue, color: '#fff', border: 'none', borderRadius: '6px',
                padding: '0.35rem 0.85rem', fontSize: '0.8rem', cursor: 'pointer', fontFamily: C.fontBody,
              }}>
                Next →
              </button>
            </div>
          )}
        </div>

        {loadError && <p style={{ color: C.red, fontSize: '0.8rem', marginTop: '0.75rem', textAlign: 'center' }}>{loadError}</p>}
      </div>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

/**
 * AdLabAccess — candidate-facing interface for the AD red-team lab.
 *
 * Props:
 *   assessmentId  — string  (required)
 *   candidateId   — string  (optional)
 *   instanceId    — string  (optional; if supplied, skip list lookup)
 */
function AdLabAccess({ assessmentId, candidateId, instanceId: instanceIdProp }) {
  const [instanceId, setInstanceId]     = useState(instanceIdProp || null);
  const [labData, setLabData]           = useState(null);
  const [accessData, setAccessData]     = useState(null);
  const [loadError, setLoadError]       = useState('');
  const [connectLoading, setConnectLoading] = useState(false);
  const [flagInput, setFlagInput]       = useState('');
  const [flagSubmitting, setFlagSubmitting] = useState(false);
  const [flagToast, setFlagToast]       = useState(null); // { ok, msg }
  const [resetLoading, setResetLoading]       = useState(false);
  const [resetToast, setResetToast]           = useState(null);
  const [vpnRegenLoading, setVpnRegenLoading] = useState(false);
  const [vpnRegenToast, setVpnRegenToast]     = useState(null);
  const [vpnDownloading, setVpnDownloading]   = useState(false);
  const [ownedNodes, setOwnedNodes]     = useState([]);

  const pollRef = useRef(null);

  // ── fetch helpers ─────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async (id) => {
    try {
      const res = await adLabsAPI.getStatus(id);
      setLabData(res.data);
      setLoadError('');
      // Restore captured flags from server
      if (res.data?.capturedFlags?.length) {
        setOwnedNodes(prev => {
          const nodes = new Set(prev);
          res.data.capturedFlags.forEach(f => { if (f.node) nodes.add(f.node); });
          return [...nodes];
        });
      }
    } catch (err) {
      setLoadError(err.response?.data?.error || 'Failed to fetch lab status');
    }
  }, []);

  const fetchAccess = useCallback(async (id) => {
    try {
      const res = await adLabsAPI.getAccess(id);
      setAccessData(res.data);
    } catch (_) {}
  }, []);

  // ── polling ───────────────────────────────────────────────────────────────

  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const startPolling = useCallback((id, ms) => {
    stopPolling();
    pollRef.current = setInterval(() => fetchStatus(id), ms);
  }, [fetchStatus, stopPolling]);

  useEffect(() => () => stopPolling(), [stopPolling]);

  useEffect(() => {
    if (!instanceId || !labData) { stopPolling(); return; }
    const st = labData.status;
    if (st === 'provisioning' || st === 'starting') {
      startPolling(instanceId, 10000);
    } else if (st === 'running') {
      startPolling(instanceId, 15000);
      fetchAccess(instanceId);
    } else {
      stopPolling();
    }
  }, [instanceId, labData?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── initial load ──────────────────────────────────────────────────────────

  const myLabPollRef = useRef(null);

  useEffect(() => {
    if (!assessmentId) return;
    if (instanceIdProp) {
      setInstanceId(instanceIdProp);
      fetchStatus(instanceIdProp);
      return;
    }

    const tryFetchLab = (attempt = 1) => {
      adLabsAPI.myLab(assessmentId).then((res) => {
        const lab = res.data?.lab;
        if (lab) {
          setInstanceId(lab.instanceId);
          fetchStatus(lab.instanceId);
          setLoadError('');
        } else if (attempt <= 3) {
          // Retry up to 3 times — the backend auto-starts the lab on first call,
          // so subsequent calls pick up the newly provisioned instance.
          myLabPollRef.current = setTimeout(() => tryFetchLab(attempt + 1), 5000);
        }
        // After 3 attempts with null, show "Lab Not Started" (no error — genuinely not provisioned)
      }).catch((err) => {
        const errMsg = err.response?.data?.error || err.message || 'Failed to load lab';
        console.error('[AdLabAccess] myLab error (attempt ' + attempt + '):', errMsg);
        setLoadError(errMsg);
        // Retry on transient errors (500, network) up to 3 times
        if (attempt <= 3 && (!err.response || err.response.status >= 500)) {
          myLabPollRef.current = setTimeout(() => tryFetchLab(attempt + 1), 5000);
        }
      });
    };

    tryFetchLab(1);

    return () => { if (myLabPollRef.current) clearTimeout(myLabPollRef.current); };
  }, [assessmentId, instanceIdProp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── handlers ──────────────────────────────────────────────────────────────

  const handleConnect = async () => {
    setConnectLoading(true);
    try {
      const res = await adLabsAPI.connect(instanceId);
      const url = res.data?.url;
      if (url) window.open(url, '_blank', 'noopener,noreferrer');
    } catch (e) {
      setLoadError('Failed to open lab: ' + (e.response?.data?.error || e.message));
    } finally {
      setConnectLoading(false);
    }
  };

  const handleFlagSubmit = async (e) => {
    e.preventDefault();
    if (!flagInput.trim()) return;
    setFlagSubmitting(true);
    setFlagToast(null);
    try {
      const res = await adLabsAPI.submitFlag(instanceId, flagInput.trim());
      const data = res.data || {};
      if (data.accepted) {
        setFlagToast({ ok: true, msg: data.message || 'Flag accepted!' });
        if (data.node) setOwnedNodes(prev => prev.includes(data.node) ? prev : [...prev, data.node]);
        setFlagInput('');
      } else {
        setFlagToast({ ok: false, msg: data.message || 'Incorrect flag.' });
      }
    } catch (err) {
      const msg = err.response?.data?.message || err.response?.data?.error || 'Submission failed. Try again.';
      setFlagToast({ ok: false, msg });
    } finally {
      setFlagSubmitting(false);
      setTimeout(() => setFlagToast(null), 5000);
    }
  };

  const handleVpnRegen = async () => {
    setVpnRegenLoading(true);
    setVpnRegenToast(null);
    try {
      await adLabsAPI.regenerateVpn(instanceId);
      setVpnRegenToast({ ok: true, msg: 'New VPN config generated — download below.' });
      // Refresh access data so vpnUrl updates
      await fetchAccess(instanceId);
    } catch (_) {
      setVpnRegenToast({ ok: false, msg: 'Regeneration failed. Contact your recruiter.' });
    } finally {
      setVpnRegenLoading(false);
      setTimeout(() => setVpnRegenToast(null), 6000);
    }
  };

  const handleVpnDownload = async () => {
    setVpnDownloading(true);
    try {
      const sessionId = localStorage.getItem('sessionId');
      const headers = {};
      if (sessionId) headers['x-session-id'] = sessionId;
      const token = localStorage.getItem('token');
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const res = await fetch(`/api/labs/ad/${instanceId}/vpn.ovpn`, { headers });
      if (!res.ok) throw new Error(await res.text());
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const disposition = res.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="([^"]+)"/);
      const filename = match ? match[1] : `sechire-lab-${instanceId}.ovpn`;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setVpnRegenToast({ ok: false, msg: 'Download failed: ' + e.message });
      setTimeout(() => setVpnRegenToast(null), 5000);
    } finally {
      setVpnDownloading(false);
    }
  };

  const handleReset = async () => {
    setResetLoading(true);
    setResetToast(null);
    try {
      // Reset is a restart of the lab containers — backend may or may not support this
      await adLabsAPI.resetLab(instanceId);
      setResetToast({ ok: true, msg: 'Lab reset requested. Please wait 2–4 minutes.' });
    } catch (_) {
      setResetToast({ ok: false, msg: 'Reset failed. Contact your recruiter.' });
    } finally {
      setResetLoading(false);
      setTimeout(() => setResetToast(null), 6000);
    }
  };

  // ── derived state ─────────────────────────────────────────────────────────

  const status        = labData?.status || null;
  const isProvisioning = status === 'provisioning' || status === 'starting';
  const isRunning     = status === 'running';
  const vpnReady      = accessData?.vpnReady ?? false;
  const vpnUrl        = accessData?.vpnDownloadUrl || (vpnReady && instanceId ? `/api/labs/ad/${instanceId}/vpn.ovpn` : null);
  const sshEntry      = accessData?.sshEntry || { host: '10.10.10.30', user: 'candidate', pass: 'LabAccess2024!' };
  const timeRemaining = labData?.timeRemainingSeconds;
  const connActive    = labData?.connections?.active ?? null;
  const connMax       = labData?.connections?.max ?? 3;
  const connAtLimit   = connActive !== null && connActive >= connMax;
  const OBJECTIVES_BY_SCENARIO = {
    'hollow-crown': [
      { label: 'Compromise WS01 (initial foothold)',   id: 'ws01' },
      { label: 'Pivot to SRV01 via MSSQL',             id: 'srv01' },
      { label: 'Compromise DC01 (Domain Admin)',        id: 'dc01' },
    ],
    'ad-lateral-movement': [
      { label: 'Gain foothold on WS01',                id: 'ws01' },
      { label: 'Pivot to SRV01 via SMB',               id: 'srv01' },
      { label: 'Compromise DC01 (Domain Admin)',        id: 'dc01' },
    ],
    'ad-kerberoasting': [
      { label: 'Kerberoast WS01',                      id: 'ws01' },
      { label: 'Compromise DC01 (Domain Admin)',        id: 'dc01' },
    ],
  };
  const objectives    = OBJECTIVES_BY_SCENARIO[accessData?.scenario] || OBJECTIVES_BY_SCENARIO['ad-lateral-movement'];
  const flagsTotal    = objectives.length;
  const flagsOwned    = ownedNodes.length;
  const progressPct   = Math.round((flagsOwned / flagsTotal) * 100);

  // ── not started ───────────────────────────────────────────────────────────

  if (!instanceId && !labData) {
    return (
      <div style={{ ...S.bleed, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '4rem', paddingBottom: '4rem' }}>
        <div style={{ textAlign: 'center', maxWidth: '420px', padding: '2rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{loadError ? '⚠️' : '⚙️'}</div>
          <h2 style={{ color: C.text, fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>
            {loadError ? 'Lab Access Error' : 'Lab Not Started'}
          </h2>
          {loadError ? (
            <>
              <p style={{ color: C.red, fontSize: '0.875rem', lineHeight: 1.5, marginBottom: '1rem' }}>
                {loadError}
              </p>
              <p style={{ color: C.textDim, fontSize: '0.8rem', lineHeight: 1.5 }}>
                Please try refreshing the page. If the issue persists, contact your recruiter.
              </p>
            </>
          ) : (
            <p style={{ color: C.textDim, fontSize: '0.875rem', lineHeight: 1.5 }}>
              Your recruiter hasn't started the lab yet. Check back shortly — you'll see the lab interface appear here automatically.
            </p>
          )}
        </div>
      </div>
    );
  }

  // ── provisioning ──────────────────────────────────────────────────────────

  if (isProvisioning) {
    return <ProvisioningScreen loadError={loadError} />;
  }

  // ── running ───────────────────────────────────────────────────────────────

  if (isRunning) {
    const sshHost = sshEntry?.host || '10.10.10.30';
    const sshUser = sshEntry?.username || sshEntry?.user || 'candidate';
    const sshPass = sshEntry?.password || sshEntry?.pass || 'LabAccess2024!';

    return (
      <div style={S.bleed}>
        <div style={S.inner}>

          {/* ── lab header ── */}
          <div style={{ marginBottom: '1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '0.35rem' }}>
              <h1 style={{ fontSize: '1.3rem', fontWeight: 700, color: C.text, margin: 0, fontFamily: C.fontHeading }}>
                {accessData?.scenarioLabel || 'AD Lab'}
              </h1>
              <span style={S.badge('green')}>
                <span style={S.pulseDot} />
                Active
              </span>
              <span style={{ ...S.badge('yellow'), marginLeft: 'auto' }}>Red Team</span>
            </div>
            <p style={{ color: C.textMuted, fontSize: '0.82rem', margin: 0 }}>
              {accessData?.scenario === 'hollow-crown'
                ? 'Multi-stage pivot across 3 VLANs to Domain Admin'
                : 'Active Directory lateral movement and privilege escalation assessment'}
            </p>
          </div>

          {/* ── two-column layout ── */}
          <div style={S.grid}>

            {/* LEFT COLUMN */}
            <div>

              {/* Timer + Connect */}
              <div style={S.card}>
                {timeRemaining != null && (
                  <div style={S.countdownBox}>
                    <div>
                      <div style={{ fontSize: '0.68rem', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
                        Time Remaining
                      </div>
                      <div style={{ fontFamily: C.mono, fontSize: '1.3rem', fontWeight: 800, color: timeRemaining < 900 ? C.red : C.blue, letterSpacing: '0.05em' }}>
                        {formatHms(timeRemaining)}
                      </div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ fontSize: '0.68rem', color: C.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '2px' }}>
                        Progress
                      </div>
                      <div style={{ fontFamily: C.mono, fontSize: '1.1rem', fontWeight: 700, color: C.text }}>
                        {flagsOwned}/{flagsTotal} <span style={{ fontSize: '0.72rem', color: C.textMuted }}>flags</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Progress bar */}
                <div style={{ marginBottom: '1.25rem' }}>
                  <div style={S.progressBar(progressPct)}>
                    <div style={S.progressFill(progressPct)} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '4px' }}>
                    <span style={{ fontSize: '0.7rem', color: C.textMuted }}>{progressPct}% complete</span>
                    <span style={{ fontSize: '0.7rem', color: C.textMuted }}>{flagsTotal - flagsOwned} remaining</span>
                  </div>
                </div>

                {/* Connection slots */}
                {connActive !== null && (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', borderRadius: '6px', marginBottom: '0.75rem',
                    background: connAtLimit ? C.redBg : C.surface2,
                    border: `1px solid ${connAtLimit ? C.red : C.border}`,
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        {Array.from({ length: connMax }).map((_, i) => (
                          <div key={i} style={{
                            width: '10px', height: '10px', borderRadius: '2px',
                            background: i < connActive ? (connAtLimit ? C.red : C.green) : C.border2,
                          }} />
                        ))}
                      </div>
                      <span style={{ fontSize: '0.78rem', color: connAtLimit ? C.redText : C.textDim, fontWeight: 600 }}>
                        {connActive} / {connMax} connections active
                      </span>
                    </div>
                    {connAtLimit && (
                      <span style={{ fontSize: '0.72rem', color: C.redText, fontWeight: 600 }}>
                        Close a tab to open a new one
                      </span>
                    )}
                  </div>
                )}

                {/* Connect button */}
                <button
                  style={{ ...S.connectBtn, opacity: (connectLoading || connAtLimit) ? 0.6 : 1 }}
                  disabled={connectLoading || connAtLimit}
                  onClick={handleConnect}
                >
                  {connectLoading
                    ? <><span style={S.spinner} /> Opening...</>
                    : <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><rect x="2" y="3" width="20" height="14" rx="2" /><path d="M8 21h8m-4-4v4" /></svg>
                        Open Lab in Browser
                      </>
                  }
                </button>

              </div>

              {/* Entry Point */}
              <div style={S.card}>
                <div style={S.sectionLabel}>Entry Point</div>
                <div style={S.entryIpBox}>
                  <span style={S.pulseDot} />
                  <span style={{ fontFamily: C.mono, fontSize: '1rem', fontWeight: 700, color: C.blue, letterSpacing: '0.05em' }}>
                    {sshHost}
                  </span>
                  <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: C.textMuted }}>WS01 · Workstation</span>
                </div>
                <div style={{ borderRadius: '7px', border: `1px solid ${C.border}`, overflow: 'hidden' }}>
                  <CredRow label="Host" value={sshHost} />
                  <CredRow label="Username" value={sshUser} />
                  <CredRow label="Password" value={sshPass} secret />
                  <div style={{ ...S.credRow, borderBottom: 'none' }}>
                    <span style={S.credLabel}>SSH</span>
                    <span style={{ ...S.credVal, color: C.textMuted }}>
                      {`ssh ${sshUser}@${sshHost}`}
                    </span>
                    <CopyButton text={`ssh ${sshUser}@${sshHost}`} />
                  </div>
                </div>
                <p style={{ fontSize: '0.72rem', color: C.textMuted, marginTop: '0.6rem', lineHeight: 1.5 }}>
                  {vpnReady
                    ? 'Use the browser terminal above for RDP/SSH access, or connect via VPN for direct SSH.'
                    : sshEntry?.note || 'Use the browser terminal above for SSH access.'}
                </p>
              </div>

              {/* Network Topology */}
              <div style={S.card}>
                <div style={S.sectionLabel}>Network Topology</div>
                <LabTopologyMap ownedNodes={ownedNodes} scenario={accessData?.scenario || 'default'} />
              </div>

            </div>

            {/* RIGHT COLUMN (sidebar) */}
            <div>

              {/* Flag submission */}
              <div style={S.card}>
                <div style={S.sectionLabel}>Submit Flag</div>
                <form onSubmit={handleFlagSubmit}>
                  <input
                    style={S.flagInput}
                    type="text"
                    placeholder="FLAG{...}"
                    value={flagInput}
                    onChange={e => setFlagInput(e.target.value)}
                    disabled={flagSubmitting}
                    autoComplete="off"
                    spellCheck={false}
                  />
                  <button
                    type="submit"
                    style={{ ...S.submitBtn, opacity: (flagSubmitting || !flagInput.trim()) ? 0.5 : 1 }}
                    disabled={flagSubmitting || !flagInput.trim()}
                  >
                    {flagSubmitting ? 'Checking...' : 'Submit Flag'}
                  </button>
                </form>
                {flagToast && <div style={S.toast(flagToast.ok)}>{flagToast.msg}</div>}
              </div>

              {/* Lab reset */}
              <div style={S.card}>
                <div style={S.sectionLabel}>Lab Reset</div>
                <p style={{ fontSize: '0.78rem', color: C.textDim, marginBottom: '0.75rem', lineHeight: 1.5 }}>
                  If the lab is unresponsive or machines are unreachable, you can request a reset. This restarts all VMs.
                </p>
                <button
                  style={{ ...S.resetBtn, opacity: resetLoading ? 0.6 : 1 }}
                  disabled={resetLoading}
                  onClick={handleReset}
                >
                  {resetLoading ? 'Requesting reset...' : '↺  Request Lab Reset'}
                </button>
                {resetToast && <div style={S.toast(resetToast.ok)}>{resetToast.msg}</div>}
              </div>

              {/* VPN Config — only shown for Docker labs that provide a VPN */}
              {vpnReady && (
              <div style={S.card}>
                <div style={S.sectionLabel}>VPN Access</div>
                <p style={{ fontSize: '0.78rem', color: C.textDim, marginBottom: '0.75rem', lineHeight: 1.5 }}>
                  Download the OpenVPN config to access the lab from your local machine via terminal.
                </p>
                <button
                  style={{ ...S.resetBtn, marginBottom: '0.5rem', opacity: vpnDownloading ? 0.6 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}
                  disabled={vpnDownloading}
                  onClick={handleVpnDownload}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                  {vpnDownloading ? 'Downloading...' : 'Download .ovpn'}
                </button>
                <button
                  style={{ ...S.resetBtn, opacity: vpnRegenLoading ? 0.6 : 1 }}
                  disabled={vpnRegenLoading}
                  onClick={handleVpnRegen}
                >
                  {vpnRegenLoading ? 'Regenerating...' : '⟳  Regenerate VPN Config'}
                </button>
                {vpnRegenToast && <div style={S.toast(vpnRegenToast.ok)}>{vpnRegenToast.msg}</div>}
              </div>
              )}

              {/* Objectives */}
              <div style={S.card}>
                <div style={S.sectionLabel}>Objectives</div>
                {objectives.map((obj) => ({ ...obj, done: ownedNodes.includes(obj.id) })).map((obj, i) => (
                  <div key={obj.id} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '8px 0',
                    borderBottom: i < objectives.length - 1 ? `1px solid ${C.border}` : 'none',
                  }}>
                    <div style={{
                      width: '18px', height: '18px', borderRadius: '50%', flexShrink: 0,
                      border: `1.5px solid ${obj.done ? C.green : C.border2}`,
                      background: obj.done ? C.greenBg : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '10px', color: C.green,
                    }}>
                      {obj.done ? '✓' : ''}
                    </div>
                    <span style={{ fontSize: '0.8rem', color: obj.done ? C.greenText : C.textDim, textDecoration: obj.done ? 'line-through' : 'none' }}>
                      {obj.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Instance info */}
              <div style={{ ...S.card, background: 'transparent', border: `1px solid ${C.border}` }}>
                <div style={S.sectionLabel}>Instance Info</div>
                {[
                  { label: 'Instance', value: instanceId },
                  { label: 'Domain',   value: accessData?.scenario === 'hollow-crown' ? 'CORP.LOCAL' : 'SECHIRE.LAB' },
                  { label: 'Scope',    value: accessData?.scenario === 'hollow-crown' ? '10.10.10.0/24, 10.20.20.0/24, 10.30.30.0/24' : '10.10.10.0/24' },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '5px 0', borderBottom: `1px solid ${C.border}` }}>
                    <span style={{ fontSize: '0.72rem', color: C.textMuted }}>{label}</span>
                    <span style={{ fontFamily: C.mono, fontSize: '0.72rem', color: C.textDim }}>{value}</span>
                  </div>
                ))}
              </div>

            </div>
          </div>

          {loadError && (
            <div style={{ ...S.toast(false), marginTop: '1rem' }}>{loadError}</div>
          )}
        </div>
      </div>
    );
  }

  // ── stopped / error ───────────────────────────────────────────────────────

  return (
    <div style={{ ...S.bleed, display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: '4rem', paddingBottom: '4rem' }}>
      <div style={{ textAlign: 'center', maxWidth: '380px', padding: '2rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🔒</div>
        <h2 style={{ color: C.text, fontSize: '1.1rem', fontWeight: 700, marginBottom: '0.5rem' }}>Lab Session Ended</h2>
        <p style={{ color: C.textDim, fontSize: '0.875rem', lineHeight: 1.5, marginBottom: '0.75rem' }}>
          Your lab session has ended or is unavailable. Contact your recruiter if you believe this is an error.
        </p>
        <span style={S.badge('red')}>{(status || 'unavailable').toUpperCase()}</span>
        {loadError && <p style={{ color: C.red, fontSize: '0.8rem', marginTop: '0.75rem' }}>{loadError}</p>}
      </div>
    </div>
  );
}

export default AdLabAccess;
